import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import {
  Badge, Button, Card, FixyBeeMark, Icon, ProviderCard, StatPill, StepCard,
  useViewport,
} from "@/ds";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import DanyHomeHero from "@/components/dany/DanyHomeHero";
import CategoryGrid from "@/components/categories/CategoryGrid";
import { useSmartCTA } from "@/hooks/useSmartCTA";
import api, { mediaUrl } from "@/lib/api";

/**
 * fixydany.com.
 *
 * The composition is Jorge's DY1.4 spec and it overrides any other reading of
 * the kits: **one hero, always Dany's.** Landing on the site you get
 * `DanyHomeHero` — greeting, ZIP-aware composer, dual chips, Pro banner. Below
 * the scroll come the marketing sections from the marketing kit WITHOUT its own
 * hero: the "Get it fixed today by a FixyBee" block and its duplicate search bar
 * are discarded entirely, because they would put a second hero and a second
 * search field on the same page.
 *
 * What survives from the marketing kit, in order: categories, how it works, the
 * trust strip, the FixyBees block, and the weekly Spotlight — the dual landing
 * DANY.md §2.5 point 3 requires to stay below the fold.
 *
 * Every section is `auto-fit`, never a fixed column count: Spanish runs ~20%
 * longer and a hard-coded track crushes the cards once it wraps.
 */

const STEPS_KEYS = ["post", "compare", "fix"];

const BEE_FEATS = [
  { icon: "map-pin", key: "jobs" },
  { icon: "shield-check", key: "profile" },
  { icon: "globe", key: "page" },
  { icon: "rocket", key: "free" },
];

const TRUST = [
  { icon: "shield-check", title: "verified", body: "verifiedDesc" },
  { icon: "star", title: "rated", body: "ratedDesc" },
  { icon: "credit-card", title: "safe", body: "safeDesc" },
];

function Section({ children, tone = "canvas", id, testId }) {
  const background = {
    canvas: "var(--canvas)",
    linen: "var(--linen)",
    worker: "var(--surface-inverse)",
  }[tone];
  return (
    <section
      id={id}
      data-testid={testId}
      style={{
        background,
        color: tone === "worker" ? "var(--text-inverse)" : "var(--text-body)",
        padding: "var(--section-padding-y) var(--section-padding-x)",
      }}
    >
      <div style={{ maxWidth: "var(--container-max)", margin: "0 auto" }}>{children}</div>
    </section>
  );
}

function SectionHead({ title, subtitle, action, inverse }) {
  return (
    <div style={{
      display: "flex", alignItems: "flex-end", justifyContent: "space-between",
      gap: "var(--space-5)", flexWrap: "wrap", marginBottom: "var(--space-8)",
    }}>
      <div>
        <h2 style={{
          fontFamily: "var(--font-display)", fontWeight: 700,
          fontSize: "var(--text-section)", lineHeight: 1.15,
          letterSpacing: "var(--tracking-tight)", maxWidth: "20ch",
        }}>{title}</h2>
        {subtitle && (
          <p style={{
            marginTop: 8, fontSize: "var(--text-base)", lineHeight: "var(--leading-relaxed)",
            color: inverse ? "var(--cream-a80)" : "var(--text-muted)", maxWidth: "var(--measure-lede)",
          }}>{subtitle}</p>
        )}
      </div>
      {action}
    </div>
  );
}

const autoFit = (min) => ({
  display: "grid",
  gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${min}px), 1fr))`,
  gap: "var(--gutter)",
});

export default function Landing() {
  const cta = useSmartCTA();
  const { t } = useTranslation();
  const { isMobile } = useViewport();
  const [spotlight, setSpotlight] = useState(null);

  useEffect(() => {
    api.get("/spotlight/current").then((r) => setSpotlight(r.data)).catch(() => {});
  }, []);

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", background: "var(--bg-page)" }} data-testid="landing-page">
      <Navbar />

      {/* The one and only hero. */}
      <DanyHomeHero />

      {/* ── Below the scroll: the marketing landing, hero excluded ───────── */}

      {/* The brand statement.
       *
       * Heading hierarchy, stated because it is a deliberate SEO decision and
       * not an accident of the redesign: the `h1` is the Dany greeting, because
       * that is what the page IS now. The proposition that used to carry the
       * `h1` — "Get it fixed today by a FixyBee you can trust" — keeps a heading
       * of its own here, as the first `h2` below the fold, so the phrase a
       * homeowner actually searches for is still a heading on the page and not
       * only a `<title>`. It sits at the same level as "Browse by category" and
       * "How FixyDany works": three sibling sections under one h1, which is the
       * structure a crawler expects.
       *
       * This band also carries `ctaPostJob`. Dany will draft and post the job
       * from the composer in DY2.1, but until then the home would have no route
       * to /client/jobs/new at all, and that link existed before the redesign.
       * For a signed-in reader it is a `secondary` on purpose — one gold
       * button per region. For a visitor with no account the band carries the
       * two sign-up doors instead (2026-10-28, Jorge's brief): "I need a
       * service" is this region's one primary, the FixyBee door its secondary. */}
      <Section tone="linen" testId="brand-section">
        <div style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "minmax(0, 1.15fr) minmax(0, 0.85fr)",
          gap: "var(--gutter)", alignItems: "center",
        }}>
          <div>
            {/* "Join free" is an offer, not a fact, and a signed-in reader has
                already taken it. The pill keeps its place in the layout either
                way — it just stops selling a membership the reader holds. */}
            <StatPill live label={cta.isLoggedIn ? t("landing.hero.livePill") : t("landing.hero.joinFree")} />
            <h2 style={{
              marginTop: "var(--space-5)", fontFamily: "var(--font-display)",
              fontWeight: 800, fontSize: "var(--text-section)", lineHeight: 1.15,
              letterSpacing: "var(--tracking-tight)", maxWidth: "20ch", textWrap: "balance",
            }}>
              <Trans
                i18nKey="landing.hero.headline"
                components={{ accent: <span style={{ color: "var(--hive-ink)" }} /> }}
              />
            </h2>
            <p style={{
              marginTop: "var(--space-4)", fontSize: "var(--text-lede-fluid)",
              lineHeight: "var(--leading-relaxed)", color: "var(--text-muted)",
              maxWidth: "var(--measure-lede)",
            }}>
              {t("landing.hero.subhead")}
            </p>
            {/* The two sign-up doors (2026-10-28), for a visitor without an
                account: the primary for the side most visitors are on, the
                secondary for FixyBees. Each page is that side's whole sign-up —
                there is no role screen after it. A signed-in reader has already
                chosen, and keeps the one errand that applies to them. */}
            {cta.isLoggedIn ? (
              <div style={{ marginTop: "var(--space-6)" }}>
                <Button as={Link} to={cta.postJob()} variant="secondary" iconRight="arrow-right" data-testid="cta-post-job">
                  {t("landing.hero.ctaPostJob")}
                </Button>
              </div>
            ) : (
              <div style={{
                marginTop: "var(--space-6)", display: "flex", flexWrap: "wrap",
                gap: "var(--space-3)", flexDirection: isMobile ? "column" : "row",
              }}>
                <Button as={Link} to="/signup/client" variant="primary" iconRight="arrow-right"
                        fullWidth={isMobile} data-testid="cta-signup-client">
                  {t("signupX.needService")}
                </Button>
                <Button as={Link} to="/signup/provider" variant="secondary"
                        fullWidth={isMobile} data-testid="cta-signup-provider">
                  {t("signupX.offerServices")}
                </Button>
              </div>
            )}
          </div>

          {/* ZIPPY, flying — the artwork the pre-redesign hero carried.
           *
           * It comes back here rather than in the hero because the hero belongs
           * to Dany now and the two must never share a face (BRIEF-FABLE.md §2:
           * ZIPPY is the mark, DanyAvatar is the assistant). In this band it does
           * what it did before: balances a text column that otherwise runs out
           * halfway across the page.
           *
           * Hidden below 768px through `useViewport`, which is how this system
           * branches on width — decoration that would push the CTA a full screen
           * down the page on a phone is not decoration, it is an obstacle. The
           * wing loop is a plain CSS animation, so the global
           * `prefers-reduced-motion` rule in index.css stops it. */}
          {!isMobile && (
            <div style={{ display: "flex", justifyContent: "center" }} data-testid="brand-zippy">
              <FixyBeeMark size={280} animated style={{ maxWidth: "min(100%, 320px)", height: "auto" }} />
            </div>
          )}
        </div>
      </Section>

      <Section testId="categories-section">
        <SectionHead
          title={t("landing.popularTitle")}
          subtitle={t("landing.popularSubtitle")}
          action={
            <Button as={Link} to="/providers" variant="ghost" size="sm" iconRight="arrow-right" data-testid="view-all-categories">
              {t("common.viewAll")}
            </Button>
          }
        />
        <CategoryGrid />
      </Section>

      <Section tone="linen" testId="how-it-works">
        <SectionHead title={t("landing.steps.title")} subtitle={t("landing.steps.subtitle")} />
        <div style={autoFit(260)}>
          {STEPS_KEYS.map((key, i) => (
            <StepCard
              key={key}
              index={i + 1}
              title={t(`landing.steps.${key}.title`)}
              body={t(`landing.steps.${key}.body`)}
            />
          ))}
        </div>
      </Section>

      {/* Trust rides on Dusk, never on gold: yellow means "act now" and cannot
          also mean "you are safe" (BRIEF-FABLE.md §2). */}
      <Section testId="trust-section">
        <SectionHead title={t("landing.trust.title")} />
        <div style={autoFit(260)}>
          {TRUST.map((item) => (
            <Card key={item.title} variant="trust" padding="lg">
              <Icon name={item.icon} size={22} style={{ color: "var(--dusk)" }} />
              <p style={{
                marginTop: 12, fontFamily: "var(--font-display)", fontWeight: 700,
                fontSize: "var(--text-lg)", letterSpacing: "var(--tracking-tight)",
              }}>{t(`landing.trust.${item.title}`)}</p>
              <p style={{
                marginTop: 6, fontSize: "var(--text-sm)", lineHeight: "var(--leading-relaxed)",
                color: "var(--text-muted)",
              }}>{t(`landing.trust.${item.body}`)}</p>
            </Card>
          ))}
        </div>
      </Section>

      <Section tone="worker" testId="for-fixybees-section">
        <div style={{ ...autoFit(320), alignItems: "center" }}>
          <div>
            <Badge tone="gold">
              {cta.isProvider ? t("landing.becomeBlock.badgeProvider") : t("landing.becomeBlock.badge")}
            </Badge>
            <h2 style={{
              marginTop: "var(--space-4)", fontFamily: "var(--font-display)", fontWeight: 700,
              fontSize: "var(--text-section)", lineHeight: 1.15,
              letterSpacing: "var(--tracking-tight)", maxWidth: "14ch",
            }}>{t("landing.becomeBlock.title")}</h2>
            <p style={{
              marginTop: 16, fontSize: "var(--text-lg)", lineHeight: "var(--leading-relaxed)",
              color: "var(--cream-a80)", maxWidth: "var(--measure-lede)",
            }}>{cta.isProvider ? t("landing.becomeBlock.subtitleProvider") : t("landing.becomeBlock.subtitle")}</p>
            <div style={{ marginTop: 32, display: "flex", gap: 12, flexWrap: "wrap" }}>
              {/* The destination already knew who was reading — `becomeFixyBee()`
                  sends a FixyBee to their dashboard. Only the label still told
                  them to become the thing they are. */}
              {/* Secondary on the dark band (REG-A, Jorge's rule 1): the
                  landing's one primary is "I need a service". `data-surface`
                  remaps the outline to cream instead of a style override. */}
              <Button as={Link} to={cta.becomeFixyBee()} variant="secondary" iconRight="arrow-right"
                      data-surface="inverse" data-testid="cta-join-hive">
                {cta.isProvider ? t("landing.becomeBlock.ctaDashboard") : t("landing.becomeBlock.ctaJoin")}
              </Button>
              <Button as={Link} to="/pricing" variant="ghost" data-surface="inverse" data-testid="cta-see-plans">
                {t("landing.becomeBlock.ctaPlans")}
              </Button>
            </div>
          </div>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))",
            gap: "var(--card-gap)",
          }}>
            {BEE_FEATS.map((feat) => {
              // Three of these four describe the product. The fourth described
              // signing up — "Empieza gratis / Crea tu cuenta sin costo" — which
              // to a FixyBee reading their own recruitment block is the same
              // wrong sentence as the CTA above it, in smaller type. For them it
              // says what the tier ladder actually is, which is the feature the
              // card was always pointing at.
              const key = feat.key === "free" && cta.isProvider ? "freeMember" : feat.key;
              return (
                <Card key={feat.key} variant="inverse" padding="md">
                  <Icon name={feat.icon} size={22} style={{ color: "var(--hive)" }} />
                  <p style={{
                    marginTop: 12, fontFamily: "var(--font-display)", fontWeight: 600,
                    fontSize: "var(--text-base)",
                  }}>{t(`landing.beeFeats.${key}.title`)}</p>
                  <p style={{
                    marginTop: 4, fontSize: "var(--text-sm)", lineHeight: "var(--leading-relaxed)",
                    color: "var(--cream-a80)",
                  }}>{t(`landing.beeFeats.${key}.body`)}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </Section>

      {/* Spotlight sits on a SOLID inverse surface, not the master gradient: any
          lemon→dark ramp goes olive at the midpoint (MIGRATION, decision 2). */}
      {spotlight && (
        <Section tone="worker" testId="spotlight-section">
          <Badge tone="gold">{t("landing.spotlightBadge")}</Badge>
          <div style={{ marginTop: "var(--space-4)", ...autoFit(320), alignItems: "center" }}>
            <div>
              <h2 style={{
                fontFamily: "var(--font-display)", fontWeight: 800,
                fontSize: "var(--text-section)", lineHeight: 1.15,
                letterSpacing: "var(--tracking-tight)", color: "var(--hive)",
              }}>{spotlight.company_name || spotlight.full_name}</h2>
              {/* Says what the card cannot: WHY this FixyBee is here this week.
                  It used to repeat the city, the rating and the job count —
                  which are the card's own chips, a hand's width to the right.
                  Text and card complement each other or one of them is wasted
                  space (DY1.6-UX, the same rule Dany follows out loud). */}
              <p style={{ marginTop: 8, fontFamily: "var(--font-mono)", fontSize: "var(--text-base)", color: "var(--cream-a80)" }}>
                {t("landing.spotlightWhy")}
              </p>
              {spotlight.public_slug && (
                <div style={{ marginTop: 24 }}>
                  <Button as={Link} to={`/pro/${spotlight.public_slug}`} variant="secondary" iconRight="arrow-right"
                          data-surface="inverse" data-testid="spotlight-view-profile">
                    {t("landing.viewProfile")}
                  </Button>
                </div>
              )}
            </div>
            <ProviderCard
              company={spotlight.company_name || spotlight.full_name}
              name={spotlight.full_name}
              city={spotlight.location_city}
              state={spotlight.location_state}
              avatarUrl={spotlight.avatar_url ? mediaUrl(spotlight.avatar_url) : undefined}
              verified={!!spotlight.is_verified}
              rating={spotlight.average_rating || 0}
              jobsCount={spotlight.completed_jobs_count || 0}
              bio={spotlight.bio}
              ctaLabel={t("providers.visit")}
              verifiedLabel={t("providers.verified")}
              newLabel={t("providers.newHere")}
              jobsLabel={t("providers.jobsDone", { count: spotlight.completed_jobs_count || 0 })}
              onVisit={spotlight.public_slug ? () => window.open(`/pro/${spotlight.public_slug}`, "_self") : undefined}
            />
          </div>
        </Section>
      )}

      <Footer />
    </div>
  );
}
